"use client";

import { useEffect } from "react";

/*
 * ホバー拡大と押下縮小をWeb Animations APIで駆動する。
 * CSS transitionは各コンポーネントのtransition utilityがscaleの時間や曲線も上書きするため、
 * どの要素でも同じ手応えになるようJSからanimateする。メニューとセレクトは離したときに
 * 開く（dropdown-menu.tsx、select.tsx）ので、押している間はどの要素も縮小したままである。
 */
const TARGET_SELECTOR = [
  "a[href]",
  "button",
  "select",
  "summary",
  '[role="button"]',
  '[role="checkbox"]',
  '[role="link"]',
  '[role="menuitem"]',
  '[role="menuitemcheckbox"]',
  '[role="menuitemradio"]',
  '[role="option"]',
  '[role="radio"]',
  '[role="switch"]',
  '[role="tab"]',
].join(", ");
const EXCLUDED_SELECTOR = 'input, textarea, :disabled, [aria-disabled="true"]';
// メニュー項目と選択肢はポップアップ内の行なのでホバー拡大しない。それ以外の要素は拡大しても
// 欠けないよう容器側（overflow-hiddenを外す、内側に余白を取る）で整える。
const HOVER_EXCLUDED_SELECTOR =
  '[role="menuitem"], [role="menuitemcheckbox"], [role="menuitemradio"], [role="option"]';

// framer-motionのspring(stiffness 600, damping 25)と同じ減衰振動をlinear()で近似する。
const SPRING_DURATION_MS = 400;
const SPRING_EASING = springEasing(600, 25, SPRING_DURATION_MS);

function springEasing(stiffness: number, damping: number, durationMs: number) {
  const omega = Math.sqrt(stiffness);
  const zeta = damping / (2 * omega);
  const omegaD = omega * Math.sqrt(1 - zeta * zeta);
  const points: string[] = [];
  for (let ms = 0; ms < durationMs; ms += 10) {
    const t = ms / 1000;
    const decay = Math.exp(-zeta * omega * t);
    const x =
      1 -
      decay *
        (Math.cos(omegaD * t) +
          ((zeta * omega) / omegaD) * Math.sin(omegaD * t));
    points.push(x.toFixed(4));
  }
  points.push("1");
  return `linear(${points.join(", ")})`;
}

// 押下は長辺80px以下で0.95、200px以上で0.98、その間は線形に補間する。
// ホバーは押下の縮小量の40%だけ拡大する（0.95→1.02、0.98→1.008）。
function scalesFor(element: HTMLElement) {
  const size = Math.max(element.offsetWidth, element.offsetHeight);
  const ratio = Math.min(Math.max((size - 80) / 120, 0), 1);
  const press = 0.95 + 0.03 * ratio;
  return { press, hover: 1 + (1 - press) * 0.4 };
}

function findTarget(target: EventTarget | null) {
  if (!(target instanceof Element)) {
    return null;
  }
  const element = target.closest<HTMLElement>(TARGET_SELECTOR);
  return element && !element.matches(EXCLUDED_SELECTOR) ? element : null;
}

const animations = new WeakMap<HTMLElement, Animation>();

function animateScale(element: HTMLElement, target: number, reduced: boolean) {
  const current = getComputedStyle(element).scale;
  animations.get(element)?.cancel();
  const animation = element.animate(
    { scale: [current === "none" ? "1" : current, String(target)] },
    {
      duration: reduced ? 0 : SPRING_DURATION_MS,
      easing: SPRING_EASING,
      fill: "forwards",
    },
  );
  if (target === 1) {
    animation.onfinish = () => animation.cancel();
  }
  animations.set(element, animation);
}

export function InteractionScale() {
  useEffect(() => {
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    let hovered: HTMLElement | null = null;
    let pressed: HTMLElement | null = null;

    const animate = (element: HTMLElement, target: number) =>
      animateScale(element, target, reducedMotion.matches);

    const setHovered = (element: HTMLElement | null) => {
      if (element === hovered) {
        return;
      }
      const previous = hovered;
      hovered = element;
      if (previous && previous !== pressed) {
        animate(previous, 1);
      }
      if (element && element !== pressed) {
        animate(element, scalesFor(element).hover);
      }
    };

    const release = () => {
      if (!pressed) {
        return;
      }
      const element = pressed;
      pressed = null;
      animate(element, element === hovered ? scalesFor(element).hover : 1);
    };

    const press = (target: EventTarget | null) => {
      release();
      const element = findTarget(target);
      if (!element) {
        return;
      }
      pressed = element;
      animate(element, scalesFor(element).press);
    };

    const onPointerOver = (event: PointerEvent) => {
      const element = findTarget(event.target);
      setHovered(
        element &&
          event.pointerType !== "touch" &&
          !element.matches(HOVER_EXCLUDED_SELECTOR)
          ? element
          : null,
      );
    };

    const onPointerOut = (event: PointerEvent) => {
      if (event.relatedTarget === null) {
        setHovered(null);
      }
    };

    const onPointerDown = (event: PointerEvent) => {
      if (event.button === 0) {
        press(event.target);
      }
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (!event.repeat && (event.key === " " || event.key === "Enter")) {
        press(event.target);
      }
    };

    document.addEventListener("pointerover", onPointerOver, true);
    document.addEventListener("pointerout", onPointerOut, true);
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("keydown", onKeyDown, true);
    window.addEventListener("pointerup", release, true);
    window.addEventListener("pointercancel", release, true);
    window.addEventListener("keyup", release, true);
    window.addEventListener("blur", release);

    return () => {
      release();
      setHovered(null);
      document.removeEventListener("pointerover", onPointerOver, true);
      document.removeEventListener("pointerout", onPointerOut, true);
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("keydown", onKeyDown, true);
      window.removeEventListener("pointerup", release, true);
      window.removeEventListener("pointercancel", release, true);
      window.removeEventListener("keyup", release, true);
      window.removeEventListener("blur", release);
    };
  }, []);

  return null;
}
